import { useCallback, useEffect, useRef, useState } from 'react';
import { format } from 'date-fns';
import { useQueryClient } from '@tanstack/react-query';
import { useOrganization } from '@/hooks/useOrganization';
import { useToast } from '@/components/ui/use-toast';
import { useAskHobbesHistory } from '@/hooks/useAskHobbesHistory';
import type {
  OrgAskRequest,
  OrgAskResponse,
} from '@/lib/api';
import {
  buildClaudeOptions,
  GENERIC_STREAM_MESSAGES,
  PROGRESS_HEADLINES,
  PROGRESS_SUBLINES,
  streamAskResponse,
  type StreamProgress,
  type ToolResultEvent,
  type ToolUseEvent,
} from '@/lib/orgAskStream';
import {
  askHobbesCache,
  cloneEntries,
  type AskHobbesEntry,
} from '@/lib/askHobbesCache';
import { queryKeys } from '@/queries/keys';

// Re-export the shared entry type for backwards compatibility
export type ChatEntry = AskHobbesEntry;

const DEFAULT_SYSTEM_PROMPT =
  'You are Hobbes, an expert SaaS product analyst. Provide balanced, actionable answers with clear structure and bullet points.';

const createId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `ask-hobbes-${Date.now()}`;
};

export interface UseAskHobbesOptions {
  /** If true, clears the conversation when organization changes */
  clearOnOrgChange?: boolean;
}

export interface UseAskHobbesReturn {
  entries: ChatEntry[];
  isSubmitting: boolean;
  hasSubmitted: boolean;
  handleSubmit: (prompt: string, includeThinking?: boolean) => Promise<void>;
  clearConversation: () => void;
  setEntries: React.Dispatch<React.SetStateAction<ChatEntry[]>>;
}

export function useAskHobbes(options: UseAskHobbesOptions = {}): UseAskHobbesReturn {
  const { clearOnOrgChange = true } = options;
  const { currentOrganization } = useOrganization();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [entries, setEntries] = useState<ChatEntry[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const hydratedOrgRef = useRef<string | null>(null);

  // Fetch persisted history from backend
  const { data: historyData } = useAskHobbesHistory(currentOrganization?.id);

  // Hydrate entries from persisted history on org change or when history data loads
  useEffect(() => {
    if (!currentOrganization?.id) {
      setEntries([]);
      setHasSubmitted(false);
      hydratedOrgRef.current = null;
      return;
    }

    // If we already hydrated for this org AND have in-memory entries, keep them
    if (hydratedOrgRef.current === currentOrganization.id) return;

    // Try the in-memory cache first (for cross-component sharing within a session)
    const cached = askHobbesCache.get(currentOrganization.id);
    if (cached && cached.length > 0) {
      setEntries(cloneEntries(cached));
      setHasSubmitted(true);
      hydratedOrgRef.current = currentOrganization.id;
      return;
    }

    // Hydrate from API response
    if (historyData?.items && historyData.items.length > 0) {
      const hydrated: ChatEntry[] = historyData.items.map((item) => ({
        id: item.id,
        prompt: item.prompt,
        createdAt: new Date(item.created_at),
        status: 'success' as const,
        response: {
          content: item.response_content ?? undefined,
          evidence: item.evidence ?? undefined,
        },
      }));
      setEntries(hydrated);
      setHasSubmitted(true);
      hydratedOrgRef.current = currentOrganization.id;
      return;
    }

    // historyData is still loading — wait for it before deciding
    if (historyData === undefined) return;

    // Query resolved with no items — mark hydrated and clear
    hydratedOrgRef.current = currentOrganization.id;
    if (clearOnOrgChange) {
      setEntries([]);
      setHasSubmitted(false);
    }
  }, [currentOrganization?.id, clearOnOrgChange, historyData]);

  // Sync entries to in-memory cache (for cross-component sharing)
  useEffect(() => {
    if (!currentOrganization?.id) return;
    askHobbesCache.set(currentOrganization.id, cloneEntries(entries));
  }, [entries, currentOrganization?.id]);

  const updateEntry = useCallback((id: string, data: Partial<ChatEntry>) => {
    setEntries((prev) => prev.map((entry) => (entry.id === id ? { ...entry, ...data } : entry)));
  }, []);

  const clearConversation = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setEntries([]);
    setHasSubmitted(false);
    setIsSubmitting(false);
    if (currentOrganization?.id) {
      askHobbesCache.delete(currentOrganization.id);
    }
  }, [currentOrganization?.id]);

  const handleSubmit = useCallback(
    async (value: string, includeThinking?: boolean) => {
      if (isSubmitting) return;

      const activePrompt = value.trim();
      if (!activePrompt || !currentOrganization?.id) return;

      const id = createId();
      const createdAt = new Date();

      setHasSubmitted(true);
      setEntries((prev) => [
        ...prev,
        {
          id,
          prompt: activePrompt,
          createdAt,
          status: 'loading',
        },
      ]);

      setIsSubmitting(true);

      // Build conversation history from previous completed entries
      const conversationHistory: Array<{ role: 'user' | 'assistant'; content: string }> = [];
      for (const entry of entries) {
        if (entry.status !== 'success' || !entry.response?.content) continue;
        conversationHistory.push({ role: 'user', content: entry.prompt });
        conversationHistory.push({ role: 'assistant', content: entry.response.content });
      }

      const payload: OrgAskRequest = {
        organizationId: currentOrganization.id,
        prompt: activePrompt,
        system_prompt: DEFAULT_SYSTEM_PROMPT,
        includeThinking: includeThinking ?? false,
        ...buildClaudeOptions(),
        ...(conversationHistory.length > 0 ? { conversationHistory } : {}),
      };

      let finalContent = '';
      let currentEvidence: OrgAskResponse['evidence'];
      let progress: StreamProgress | undefined;
      let streamFailed = false;

      const syncResponse = () =>
        updateEntry(id, {
          status: finalContent ? 'success' : 'loading',
          response: {
            content: finalContent || undefined,
            evidence: currentEvidence,
          },
          progress: progress ? { ...progress } : undefined,
        });

      const markFailure = (message: string) => {
        streamFailed = true;
        progress = undefined;
        finalContent = '';
        updateEntry(id, {
          status: 'error',
          errorMessage: message,
          progress: undefined,
        });
        toast({
          variant: 'destructive',
          title: 'Ask Hobbes failed',
          description: message,
        });
      };

      const setProgress = (
        phase: StreamProgress['phase'],
        primaryMessage?: string,
        extraDetails?: string
      ) => {
        const randomFrom = (arr: string[]) => arr[Math.floor(Math.random() * arr.length)];
        const trimmedPrimary = primaryMessage?.trim();
        const trimmedExtra = extraDetails?.trim();
        const normalizedPrimary = trimmedPrimary?.toLowerCase();
        const normalizedExtra = trimmedExtra?.toLowerCase();
        const meaningfulPrimary =
          trimmedPrimary && (!normalizedPrimary || !GENERIC_STREAM_MESSAGES.has(normalizedPrimary))
            ? trimmedPrimary
            : undefined;
        const meaningfulExtra =
          trimmedExtra && (!normalizedExtra || !GENERIC_STREAM_MESSAGES.has(normalizedExtra))
            ? trimmedExtra
            : undefined;

        progress = {
          phase,
          headline: randomFrom(PROGRESS_HEADLINES[phase]),
          subline: meaningfulPrimary || meaningfulExtra || randomFrom(PROGRESS_SUBLINES[phase]),
          timestamp: Date.now(),
        };
        syncResponse();
      };

      const clearProgress = () => {
        if (progress) {
          progress = undefined;
          syncResponse();
        }
      };

      try {
        const streamed = await streamAskResponse(payload, {
          onInitialContent: (text) => {
            setProgress('initial_content', text);
          },
          onToolUse: (event: ToolUseEvent) => {
            setProgress('tool_use', event.message);
          },
          onToolResult: (event: ToolResultEvent) => {
            if (event.status === 'error') {
              setProgress('tool_result', 'Query failed', event.message || 'The SQL query could not be executed.');
              return;
            }

            const extras: string[] = [];
            if (event.row_count !== undefined && event.row_count !== null) {
              extras.push(`Rows inspected: ${event.row_count}`);
            }
            if (event.result_preview && typeof event.result_preview === 'string') {
              extras.push(event.result_preview);
            }
            setProgress('tool_result', event.message, extras.length ? extras.join(' · ') : undefined);
          },
          onFinalContent: (text) => {
            const trimmed = text.trim();
            const normalized = trimmed.toLowerCase();
            if (!GENERIC_STREAM_MESSAGES.has(normalized) && trimmed.length > 0) {
              finalContent = text;
            }
            clearProgress();
          },
          onEvidence: (items) => {
            if (Array.isArray(items)) {
              currentEvidence = [...(currentEvidence ?? []), ...items];
            } else if (items) {
              currentEvidence = [...(currentEvidence ?? []), items];
            }
            syncResponse();
          },
          onError: (message) => {
            markFailure(message || 'Something went wrong while generating insights.');
          },
        });

          if (!streamFailed) {
            finalContent = streamed.content ?? finalContent;
            currentEvidence = streamed.evidence ?? currentEvidence;

            updateEntry(id, {
              status: 'success',
              response: {
                content: finalContent || undefined,
                evidence: currentEvidence,
              },
              progress: progress ? { ...progress } : undefined,
            });

            // Invalidate history query so the persisted entry (written by backend)
            // is picked up on next fetch / page reload.
            if (currentOrganization?.id) {
              queryClient.invalidateQueries({
                queryKey: queryKeys.askHobbesHistory.list(currentOrganization.id),
              });
            }
          }
      } catch (error) {
        if (!streamFailed) {
          const message = error instanceof Error ? error.message : 'Unexpected error';
          updateEntry(id, { status: 'error', errorMessage: message, progress: undefined });
          toast({
            variant: 'destructive',
            title: 'Ask Hobbes failed',
            description: message,
          });
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [isSubmitting, currentOrganization?.id, entries, updateEntry, toast, queryClient]
  );

  return {
    entries,
    isSubmitting,
    hasSubmitted,
    handleSubmit,
    clearConversation,
    setEntries,
  };
}

// Utility functions for rendering evidence cards
export const formatEvidenceDate = (value?: string | null) => {
  if (!value) return undefined;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return undefined;
  return format(parsed, 'MMM d, yyyy h:mm a');
};

export const formatMetricValue = (value: unknown) => {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value % 1 === 0 ? value.toString() : value.toFixed(2);
  }
  const numeric = Number(value);
  if (Number.isFinite(numeric)) {
    return numeric % 1 === 0 ? numeric.toString() : numeric.toFixed(2);
  }
  return String(value);
};

export const getBuyingIntentBadgeClassName = (intent?: string) => {
  if (!intent) return '';
  const normalized = intent.toLowerCase();
  if (normalized === 'high') return 'bg-green-100 text-green-700 border-green-200';
  if (normalized === 'medium') return 'bg-yellow-100 text-yellow-700 border-yellow-200';
  if (normalized === 'low') return 'bg-red-100 text-red-700 border-red-200';
  return '';
};

export const cleanResponseContent = (content?: string) => {
  if (!content) return content;
  let cleaned = content.replace(/,\s*"(topics_discussed|next_steps|objection_types|email|qualification_status|booked_status|buying_intent|engagement_level|sentiment)".*$/s, '');
  cleaned = cleaned.replace(/\s*[}\]]+\s*$/g, '');
  return cleaned.trim();
};
